// Ported from vlookup-dramatic/app/about/page.tsx by scripts/revamp/port-design.js
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import Image from "../../components/image";
import { ArrowRight, ArrowUpRight, Play } from "../../lib/icons";
import "../../styles/app-pages/listings.css";
import { aboutStory, aboutBelief, aboutCta, aboutHero, journeySection, milestones, team, teamSection, } from "../../lib/content";
import { ButtonLink } from "../../components/button-link";
import { LinkedInLogo } from "../../components/linkedin-logo";
import { Reveal } from "./reveal";
import { MilestoneRail } from "./milestone-rail";
import * as homePageActions from "../../../actions/homePageActions";
import { usePageMeta } from "../../hooks/use-page-meta";
import { useLoggedIn } from "../../hooks/use-logged-in";
import { agent } from "../../../utils/agent";
import { GET_STATS } from "../../../utils/apiPath";

/* ── Live data (revamp plan task 9 / "Existing API Wiring" sheet) ─────────────
   Same calls the old About page made:
   - GET pages/aboutus  -> homePageData (meta title/description; the CMS body is
                           Bootstrap rich-text with no slot in this design, so it
                           is not rendered — same decision as Home)
   - GET aboutus/frontend/Team + /Advisor (dispatched by loadHomePageData) -> teams, advisors
   - GET setting (loaded by the shell) -> the four hero numbers
   The design's sample data is the fallback while a call loads / returns nothing. */
const fmt = (n) => Number(n).toLocaleString("en-US");
const statsFromSetting = (s) => s && s.mentors != null
  ? [
    { n: fmt(s.mentors), l: "Mentors" },
    { n: fmt(s.mentees) + "+", l: "Mentees" },
    { n: fmt(s.organisations) + "+", l: "Organisations" },
    { n: fmt(s.mentoring_hours) + "+", l: "Mentoring hours" },
  ]
  : aboutHero.stats;
// API person -> design card (QA row 68: no badge pill — role stays in `title`).
const personFromApi = () => (m) => ({
  name: m.full_name,
  title: [m.position, m.organization].filter(Boolean).join(" · "),
  img: m.image,
  linkedin: m.linkedin_url,
  about: m.about,
  position: m.position,
  organization: m.organization,
  main_area: m.main_area,
});
// QA row 66: Kanchana Gupta's name in the story links to her LinkedIn profile.
const KG_URL = "https://www.linkedin.com/in/kanchana-gupta-6041071/";
const KG_NAME = "Kanchana Gupta";
const linkifyKG = (p) => {
  const i = p.indexOf(KG_NAME);
  if (i === -1)
    return p;
  return (<>
    {p.slice(0, i)}
    <a href={KG_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-navy underline decoration-brand-navy/30 underline-offset-2 transition-colors hover:text-brand-red">
      {KG_NAME}
    </a>
    {p.slice(i + KG_NAME.length)}
  </>);
};
// Editorial emphasis splits for the final-deck headings (content is verbatim
// from lib/content; only the italic treatment is decided here).
const journeyWords = journeySection.heading.split(" ");
const journeyEm = journeyWords.pop();
const journeyLead = journeyWords.join(" ");
const [teamLead, teamRest] = teamSection.heading.split(" behind ");
const [ctaLead, ctaEm] = aboutCta.heading.split(" – ");
export default function AboutPage() {
  const loggedIn = useLoggedIn();
  const dispatch = useDispatch();
  const [teamMember, setTeamMember] = useState(null);
  const homePageData = useSelector((st) => st.homePageData) || {};
  const setting = useSelector((st) => st.setting);
  const teamsApi = useSelector((st) => st.teams) || [];
  useEffect(() => {
    dispatch(homePageActions.loadHomePageData("aboutus")); // also loads teams + advisors
  }, [dispatch]);
  usePageMeta({
    title: homePageData.meta_title || "About Us | vLookUp",
    description: homePageData.meta_description || aboutStory.paras[0],
  });
  // Dynamic stats ticker (GET get-stats?category=About)
  const [dynamicStats, setDynamicStats] = useState(null);
  useEffect(() => {
    let alive = true;
    agent
      .get(`${GET_STATS}?category=About`)
      .then((res) => {
        if (!alive) return;
        const raw = res?.data?.data ?? res?.data;
        const rows = Array.isArray(raw) ? raw : [];
        const aboutRows = rows
          .filter((r) => {
            const isActive = r && r.status !== false && r.status !== 0 && r.status !== "false" && r.status !== "0";
            const isAbout = !r?.category || String(r.category).trim().toLowerCase() === "about";
            return isActive && isAbout && r.heading && r.count != null;
          })
          .sort((a, b) => {
            if (a.display_order != null && b.display_order != null) {
              return Number(a.display_order) - Number(b.display_order);
            }
            return 0;
          })
          .map((r) => ({
            id: r.id,
            n: r.count,
            l: r.heading,
          }));

        if (aboutRows.length > 0) {
          setDynamicStats(aboutRows);
        }
      })
      .catch(() => { });

    return () => {
      alive = false;
    };
  }, []);

  const stats = (dynamicStats && dynamicStats.length > 0)
    ? dynamicStats
    : statsFromSetting(setting);
  const teamCards = teamsApi.length ? teamsApi.map(personFromApi()) : team;
  return (<div>
    {/* Scroll-reveal styles — hidden state only applies when JS can run;
            reduced-motion users always see content instantly. */}
    <style>{`
        @media (scripting: enabled) {
          [data-about-reveal] {
            opacity: 0;
            transform: translateY(26px);
            transition: opacity 0.7s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
          }
          [data-about-reveal][data-revealed] {
            opacity: 1;
            transform: none;
          }
        }
        .about-founder-logos {
          margin-top: 4.75rem;
          text-align: center;
        }
        .about-founder-logos .eyebrow {
          margin: 0 0 0.4rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c4342d;
        }
        .about-founder-logos h2 {
          margin: 0 0 1.35rem;
          font-family: inherit;
          font-size: 1.25rem;
          font-weight: 600;
          line-height: 1.25;
          letter-spacing: -0.01em;
          color: #1a3a5c;
        }
        .about-founder-logos-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 1.75rem 2.25rem;
        }
        .about-founder-logos a {
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.7;
          filter: grayscale(1);
          transition: opacity 0.2s ease, filter 0.2s ease;
        }
        .about-founder-logos a:hover,
        .about-founder-logos a:focus-visible {
          opacity: 1;
          filter: grayscale(0);
        }
        .about-founder-logos a.has-color-swap {
          position: relative;
          opacity: 0.85;
          filter: none;
        }
        .about-founder-logos a.has-color-swap .is-color {
          position: absolute;
          left: 0;
          top: 0;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .about-founder-logos a.has-color-swap:hover .is-color,
        .about-founder-logos a.has-color-swap:focus-visible .is-color {
          opacity: 1;
        }
        .about-founder-logos img {
          width: auto;
          object-fit: contain;
        }
        .about-founder-logos img.is-stack {
          height: 4.25rem;
          max-width: 7rem;
        }
        .about-founder-logos img.is-mark {
          height: 3.25rem;
          max-width: 6.5rem;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-about-reveal] {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
        }
      `}</style>

    {/* ============ FIRST SCROLL — THE VLOOKUP STORY ============ */}
    <section className="relative isolate overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -right-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-brand-ink/25 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -left-32 top-44 h-80 w-80 rounded-full bg-brand-ink/25 blur-3xl" />

      <div className="wrap relative grid items-center gap-12 pb-12 pt-14 md:grid-cols-[1.04fr_0.96fr] md:pt-20 lg:gap-16">
        {/* Left — the story */}
        <Reveal>
          <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
            <span className="h-px w-8 bg-current opacity-50" />
            {aboutStory.eyebrow}
          </p>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.03] text-heading md:text-6xl lg:text-7xl">
            Mentorship shouldn&rsquo;t depend on{" "}
            <em className="font-normal italic text-brand-red">privilege</em>
          </h1>
          <div className="mt-6 max-w-xl space-y-4">
            {aboutStory.paras.map((p) => p.length <= 60 ? (
              // the short pivot line gets an editorial pull-quote treatment
              <p key={p} className="font-display text-2xl font-medium italic leading-snug text-brand-navy">
                {p}
              </p>) : (<p key={p} className="text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                {linkifyKG(p)}
              </p>))}
          </div>
          <p className="mt-7 border-t border-brand-navy/10 pt-5 text-xs font-bold uppercase tracking-[0.14em] text-brand-navy/60">
            {aboutHero.founded}
          </p>
        </Reveal>

        {/* Right — embedded film in an editorial frame */}
        <Reveal delay={150} className="mx-auto w-full max-w-[560px]">
          <div className="relative">
            <div className="relative aspect-video overflow-hidden rounded-[1.75rem] bg-brand-ink shadow-[0_30px_80px_-30px_rgba(26,58,92,0.55)] ring-1 ring-brand-navy/10 md:rounded-[2.25rem]">
              <iframe src={aboutStory.videoUrl} title="The vLookUp story — video" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="absolute inset-0 h-full w-full border-0" />
            </div>
            <a href={aboutStory.videoWatchUrl} target="_blank" rel="noopener noreferrer" className="group absolute -bottom-4 left-6 flex items-center gap-2 rounded-full bg-brand-red px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-white shadow-lg transition-colors hover:bg-brand-red-dark">
              <Play className="size-3.5 fill-current" aria-hidden />
              Watch the vLookUp story
              <ArrowUpRight className="size-3.5 transition-transform group-hover:rotate-45" aria-hidden />
            </a>
          </div>
          {aboutStory.founderLogos && aboutStory.founderLogos.length > 0 ? (
            <div className="about-founder-logos">
              {aboutStory.founderLogosEyebrow ? (
                <p className="eyebrow">{aboutStory.founderLogosEyebrow}</p>
              ) : null}
              {/* {aboutStory.founderLogosHeading ? (
                  <h2 className="font-display">{aboutStory.founderLogosHeading}</h2>
                ) : null} */}
              <div className="about-founder-logos-row">
                {aboutStory.founderLogos.map((logo) => (
                  <a
                    key={logo.href}
                    href={logo.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={logo.label}
                    aria-label={logo.label}
                    className={logo.greySrc ? "has-color-swap" : undefined}
                  >
                    <img
                      src={logo.greySrc || logo.src}
                      alt={logo.alt}
                      className={logo.stack ? "is-stack" : "is-mark"}
                    />
                    {logo.greySrc ? (
                      <img
                        src={logo.src}
                        alt=""
                        aria-hidden="true"
                        className={(logo.stack ? "is-stack" : "is-mark") + " is-color"}
                      />
                    ) : null}
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </Reveal>
      </div>

      {/* Floating stat band */}
      <div className="wrap relative pb-14 md:pb-16">
        <Reveal delay={80}>
          {/* QA row 65: x-gap + tighter numerals so the figures don't touch
                the box edges on phones */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 rounded-3xl border border-brand-navy/10 bg-card px-4 py-8 text-center shadow-[0_24px_60px_-40px_rgba(26,58,92,0.45)] sm:grid-cols-4 sm:px-6 md:px-12">
            {stats.map((s, idx) => (<div key={s.id != null ? `${s.id}-${idx}` : `${s.l}-${idx}`} className="min-w-0">
              <div className="font-display text-3xl font-bold text-brand-blue sm:text-4xl md:text-5xl">
                {s.n}
              </div>
              <div className="mt-2 text-xs font-bold uppercase tracking-[0.1em] text-brand-navy/60">
                {s.l}
              </div>
            </div>))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* ============ SECOND SCROLL — OUR BELIEF (light panel + collage) ============ */}
    <section className="relative isolate py-4 md:py-8">

      <div className="wrap">
        <div className="relative rounded-[2.5rem] bg-cream-deep px-7 py-12 ring-1 ring-brand-navy/5 md:px-14 md:py-16">
          {/* decorative shapes clipped to the panel */}
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-[2.5rem]">
            <div className="absolute -bottom-24 -left-16 size-80 rounded-full bg-brand-ink/25" />
            <div className="absolute -right-24 -top-24 size-72 rounded-full bg-brand-ink/25" />
          </div>

          <div className="relative grid gap-12 md:grid-cols-[0.95fr_1.05fr] md:items-center lg:gap-16">
            <Reveal>
              <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
                <span className="h-px w-8 bg-current opacity-50" />
                {aboutBelief.eyebrow}
              </p>
              <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-heading md:text-4xl lg:text-5xl">
                Every woman deserves access to someone who&rsquo;s{" "}
                <em className="font-normal italic text-brand-red">
                  walked the path
                </em>{" "}
                before.
              </h2>
              <div className="mt-6 space-y-5">
                {aboutBelief.paras.map((p) => p.length <= 60 ? (<p key={p} className="font-display text-xl font-medium italic leading-snug text-brand-navy md:text-2xl">
                  {p}
                </p>) : (<p key={p} className="text-base leading-relaxed text-muted-foreground md:text-lg">
                  {p}
                </p>))}
              </div>
            </Reveal>

            {/* Overlapping photo collage from past events */}
            <Reveal delay={140}>
              <div className="relative mx-auto grid w-full max-w-[500px] grid-cols-2 items-start gap-4 md:gap-5">
                {aboutBelief.collage.map((c, i) => {
                  const shapes = [
                    "aspect-[3/4] -rotate-2",
                    "aspect-[3/4] mt-10 rotate-2",
                    "aspect-[4/3] -mt-8 rotate-1",
                    // last tile hangs over the panel's bottom edge
                    "aspect-[4/3] -rotate-2 translate-y-5 md:translate-y-8",
                  ];
                  return (<figure key={c.img} className={`relative overflow-hidden rounded-[1.5rem] shadow-[0_20px_50px_-20px_rgba(26,58,92,0.4)] ring-1 ring-brand-navy/10 ${shapes[i % shapes.length]}`}>
                    <Image src={c.img} alt={c.caption} fill sizes="(max-width: 768px) 45vw, 250px" className="object-cover" />
                  </figure>);
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* ============ OUR JOURNEY — compressed horizontal timeline ============ */}
    <section className="relative overflow-hidden py-14 md:py-20">
      <div className="wrap">
        <Reveal className="max-w-2xl">
          <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
            <span className="h-px w-8 bg-current opacity-50" />
            {journeySection.eyebrow}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.05] text-heading md:text-5xl">
            {journeyLead}{" "}
            <em className="font-normal italic text-brand-red">{journeyEm}</em>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {journeySection.sub}
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-8 md:mt-10">
          <MilestoneRail items={milestones} />
        </Reveal>
      </div>
    </section>

    {/* ============ MEET THE TEAM ============ */}
    <section className="relative overflow-hidden py-14 md:py-20">
      <div className="wrap">
        <Reveal className="max-w-2xl">
          <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
            <span className="h-px w-8 bg-current opacity-50" />
            {teamSection.eyebrow}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.05] text-heading md:text-5xl">
            {teamRest ? (<>
              {teamLead}{" "}
              <em className="font-normal italic text-brand-red">behind</em>{" "}
              {teamRest}
            </>) : (teamSection.heading)}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {teamSection.sub}
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-10 min-w-0">
          <TeamGrid cards={teamCards} onReadMore={setTeamMember} />
        </Reveal>

        {/* Advisors section removed per QA (Testing row 74, 2 Sep). */}
      </div>
    </section>

    {/* ============ CLOSING CTA — kept tasteful; the footer carries the primary join band ============ */}
    {!loggedIn && <section className="wrap pb-20">
      <Reveal>
        <div className="flex flex-col gap-8 rounded-[2rem] bg-cream-deep px-8 py-12 md:flex-row md:items-center md:justify-between md:px-14">
          <div className="max-w-xl">
            <p className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
              <span className="h-px w-8 bg-current opacity-50" />
              Get started
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-heading md:text-4xl">
              {ctaEm ? (<>
                {ctaLead} –{" "}
                <em className="font-normal italic text-brand-red">
                  {ctaEm}
                </em>
              </>) : (aboutCta.heading)}
            </h2>
            <p className="mt-3 text-muted-foreground">{aboutCta.sub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <ButtonLink href={aboutCta.primary.href} className="group h-auto items-center gap-3 rounded-full bg-brand-red py-3 pl-6 pr-3 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark">
              {aboutCta.primary.label}
              <span className="flex size-8 items-center justify-center rounded-full bg-white/20 transition-transform group-hover:rotate-45">
                <ArrowUpRight className="size-4" />
              </span>
            </ButtonLink>
            <ButtonLink href={aboutCta.secondary.href} variant="outline" className="h-auto rounded-full border-brand-navy/20 bg-transparent px-6 py-3 text-sm font-bold text-brand-navy hover:bg-brand-navy hover:text-cream">
              {aboutCta.secondary.label}
            </ButtonLink>
          </div>
        </div>
      </Reveal>
    </section>}

    {teamMember && (
      <TeamBioModal person={teamMember} onHide={() => setTeamMember(null)} />
    )}
  </div>);
}

function TeamGrid({ cards, onReadMore }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 sm:gap-x-4 md:grid-cols-4 lg:grid-cols-6 lg:gap-x-4">
      {cards.map((m) => (
        <PersonCard key={m.name} person={m} onReadMore={onReadMore} />
      ))}
    </div>
  );
}

function PersonCard({ person: m, onReadMore }) {
  return (
    <article className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-brand-navy/10 bg-card shadow-sm transition-shadow duration-300 hover:shadow-md">
      <div className="relative aspect-square shrink-0 overflow-hidden bg-gradient-to-br from-cream-deep to-brand-blue/20">
        <Image
          src={m.img}
          alt={m.name}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
          className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5">
        <h3 className="font-display text-[15px] font-semibold leading-snug text-heading sm:text-base">
          {m.name}
        </h3>
        <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
          {m.title}
        </p>
        <div className="mt-auto flex flex-col items-start gap-1.5 pt-3">
          {m.linkedin && (
            <a
              href={m.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-bold text-[#0A66C2] transition-opacity hover:opacity-80"
            >
              <LinkedInLogo className="size-3.5" />
              LinkedIn
            </a>
          )}
          <button
            type="button"
            onClick={() => onReadMore(m)}
            className="inline-flex cursor-pointer items-center gap-1 text-xs font-bold text-brand-red transition-colors hover:text-brand-red-dark"
          >
            Read more
            <ArrowRight className="size-3" />
          </button>
        </div>
      </div>
    </article>
  );
}

function TeamBioModal({ person, onHide }) {
  const aboutHtml = person.about
    ? { __html: person.about }
    : { __html: "" };
  return (
    <Modal show onHide={onHide} size="lg" centered className="vl-mentor-modal">
      <Modal.Header closeButton />
      <Modal.Body>
        <div className="vl-mmodal">
          <div className="vl-mmodal__photo">
            {person.img && <img src={person.img} alt={person.name || ""} style={{ objectPosition: "top center" }} />}
          </div>
          <div className="vl-mmodal__body">
            <span className="vl-mmodal__eyebrow">The Team</span>
            <h3 className="vl-mmodal__name">{person.name}</h3>
            {person.position && <p className="vl-mmodal__role">{person.position}</p>}
            {person.organization && <p className="vl-mmodal__org">{person.organization}</p>}
            {person.main_area && <p className="vl-mmodal__org">{person.main_area}</p>}
            <p className="vl-mmodal__label">About me</p>
            <div className="vl-mmodal__about" dangerouslySetInnerHTML={aboutHtml} />
            {person.linkedin && (
              <div className="vl-mmodal__actions">
                <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="vl-btn vl-btn--outline">
                  <i className="fab fa-linkedin-in"></i> LinkedIn profile
                </a>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
